import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { EditorState, ContentState, convertToRaw } from "draft-js";
import draftToHtml from "draftjs-to-html";
import htmlToDraft from "html-to-draftjs";
import { Editor } from "react-draft-wysiwyg";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "../../assets/css/TextEditorCSSOverride.css";
import { makeStyles } from "@material-ui/core/styles";
import { grayColor } from "../../assets/jss/material-dashboard-pro-react";

const styles = () => {
  return {
    wrapper: {
      // margin: "2px 0",
      border: "1px solid #f1f1f1",
      // padding: "5px",
      borderRadius: "4px",
    },
    toolbar: {
      border: "none",
      borderBottom: "1px solid #f1f1f1",
    },
    editor: {
      fontSize: "12px",
      lineHeight: "1.4",
      minHeight: "200px",
      padding: "4px",
      backgroundColor: grayColor[12],
    },
  };
};

export default function TextEditor(props) {
  const useStyles = makeStyles(styles);
  const classes = useStyles();

  const [editorState, setEditorState] = useState(EditorState.createEmpty());

  const { initialContent, readOnly, setContent, mentionSuggestions } = props;

  useEffect(() => {
    if (initialContent) {
      const blocksFromHtml = htmlToDraft(initialContent);
      const { contentBlocks, entityMap } = blocksFromHtml;
      const contentState = ContentState.createFromBlockArray(
        contentBlocks,
        entityMap,
      );
      const newEditorState = EditorState.createWithContent(contentState);
      setEditorState(newEditorState);
    }
  }, [initialContent]);

  const onEditorStateChange = (newState) => {
    setEditorState(newState);
    setContent(draftToHtml(convertToRaw(newState.getCurrentContent())));
  };

  // Build mention config from suggestions
  const mentionConfig = useMemo(() => {
    if (!mentionSuggestions || mentionSuggestions.length === 0) {
      return undefined;
    }

    return {
      separator: " ",
      trigger: "@",
      suggestions: mentionSuggestions.map((input) => ({
        text: input.meta_name,
        value: input.meta_name,
        url: "#", // Required but not used
      })),
    };
  }, [mentionSuggestions]);

  // Custom mention decorator to render mentions as bold
  const customDecorators = useMemo(() => {
    if (!mentionSuggestions || mentionSuggestions.length === 0) {
      return undefined;
    }
    return [
      {
        strategy: (contentBlock, callback) => {
          // Find mentions in the content
          contentBlock.findEntityRanges((character) => {
            const entityKey = character.getEntity();
            if (entityKey !== null) {
              const contentState = editorState.getCurrentContent();
              const entity = contentState.getEntity(entityKey);
              return entity.getType() === "MENTION";
            }
            return false;
          }, callback);
        },
        component: (componentProps) => (
          <strong style={{ fontWeight: 700 }}>{componentProps.children}</strong>
        ),
      },
    ];
  }, [mentionSuggestions, editorState]);

  return (
    <Editor
      editorState={editorState}
      readOnly={readOnly}
      toolbarHidden={readOnly}
      wrapperClassName={classes.wrapper}
      toolbarClassName={classes.toolbar}
      editorClassName={classes.editor}
      onEditorStateChange={onEditorStateChange}
      toolbar={{
        options: [
          "inline",
          "blockType",
          "fontSize",
          "list",
          "remove",
          "history",
        ],
      }}
      mention={mentionConfig}
      customDecorators={customDecorators}
    />
  );
}

TextEditor.defaultProps = {
  readOnly: true,
  mentionSuggestions: [],
};

TextEditor.propTypes = {
  initialContent: PropTypes.string,
  readOnly: PropTypes.bool,
  setContent: PropTypes.func.isRequired,
  mentionSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      meta_name: PropTypes.string,
    }),
  ),
};
